import {
  AutocompleteInputChangeReason,
  ListItem,
  ListItemText,
  SxProps,
} from "@mui/material";
import { SyntheticEvent, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { TalentStatus } from "../../../../../features/talents/types/common";
import { Nullable } from "../../../../types/nullable";
import LoadingSpinner from "../../loadingSpinner/LoadingSpinner";
import { AutocompleteBaseSingle } from "../autocompleteBase/AutocompleteBaseSingle";
import { TalentAutocompleteOption } from "./talentAutocompleteUtils";
import useTalentAutocomplete from "./useTalentAutocomplete";

type Props = {
  value: Nullable<TalentAutocompleteOption>;
  onChange: (value: Nullable<TalentAutocompleteOption>) => void;
  disabled?: boolean;
  placeholder?: string;
  sx?: SxProps;
  helperText?: string;
  status?: TalentStatus;
};

const getOptionLabel = (option: TalentAutocompleteOption) => option.name;

const TalentAutocomplete = ({
  value,
  onChange,
  disabled = false,
  placeholder,
  sx,
  helperText,
  status,
}: Props) => {
  const { t } = useTranslation("common");
  const [inputValue, setInputValue] = useState("");
  const { talentOptions, isLoading, setSearchTerm, resetSearch } =
    useTalentAutocomplete(status);
  const selectedLabelRef = useRef(value ? getOptionLabel(value) : "");

  useEffect(() => {
    const label = value ? getOptionLabel(value) : "";
    selectedLabelRef.current = label;
    if (!value) {
      setInputValue("");
      resetSearch();
    }
  }, [value, resetSearch]);

  const handleInputChange = (
    _: SyntheticEvent,
    newValue: string,
    reason: AutocompleteInputChangeReason,
  ) => {
    if (reason === "reset") {
      return;
    }

    setInputValue(newValue);

    if (newValue.trim()) {
      setSearchTerm(newValue);
    } else {
      resetSearch();
    }
  };

  const handleChange = (newValue: TalentAutocompleteOption | null) => {
    const label = newValue ? getOptionLabel(newValue) : "";
    selectedLabelRef.current = label;
    setInputValue(label);
    resetSearch();
    onChange(newValue);
  };

  const handleClose = () => {
    setInputValue(selectedLabelRef.current);
    resetSearch();
  };

  const autocompleteOptions =
    value && !talentOptions.some((option) => option.id === value.id)
      ? [value, ...talentOptions]
      : talentOptions;

  return (
    <AutocompleteBaseSingle<TalentAutocompleteOption>
      value={value}
      onChange={handleChange}
      options={autocompleteOptions}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={(option, selected) => option.id === selected.id}
      filterOptions={(options) => options}
      loading={isLoading}
      loadingText={<LoadingSpinner />}
      disabled={disabled}
      placeholder={placeholder}
      noOptionsText={t("writeToSearch")}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      helperText={helperText}
      onClose={handleClose}
      renderOption={(props, option) => (
        <ListItem {...props} key={option.id}>
          <ListItemText
            primary={option.name}
            primaryTypographyProps={{ noWrap: true }}
          />
        </ListItem>
      )}
      sx={{
        "& .MuiAutocomplete-input": {
          overflow: "hidden",
          textOverflow: "ellipsis",
        },
        ...sx,
      }}
    />
  );
};

export default TalentAutocomplete;
